import type { QueryParams } from 'src/pages/tenants/tenants.types';
import type { IQueryParamsState } from 'src/pages/contacts/contacts.types';

import { toast } from 'sonner';
import { useBoolean } from 'minimal-shared/hooks';
import { useState, useEffect, useCallback } from 'react';

import { useTranslate } from 'src/locales';
import { defaultFilter } from 'src/pages/tenants/tenants.types';

import {
  type IFieldItem,
  useCreateCustomFieldMutation,
  useUpdateCustomFieldMutation,
  useFilterCustomFieldsMutation,
  useDeleteCustomFieldsMutation,
} from '../components/custom-fields/slice/customFieldApi';

import type { IHandleSubmitData } from '../components/custom-fields/hooks/use-custom-field-form';

interface IUseCustomFieldProps {
  moduleName: string;
  setSelectedRecord?: React.Dispatch<React.SetStateAction<IFieldItem | undefined>>;
  sortFldList?: string[];
  isCustomFieldPage?: boolean;
}

export function useCustomField({
  moduleName,
  setSelectedRecord,
  sortFldList = [],
  isCustomFieldPage = false,
}: IUseCustomFieldProps) {
  const { t } = useTranslate('translation');
  const customFieldMenu = useBoolean();

  const [getCustomFields, { isLoading: isFetching, error: fetchError }] =
    useFilterCustomFieldsMutation();
  const [addCustomField, { isLoading: isCreating, error: createError }] =
    useCreateCustomFieldMutation();
  const [updateCustomField, { isLoading: isUpdating, error: updateError }] =
    useUpdateCustomFieldMutation();
  const [deleteCustomFields, { isLoading: isDeleting, error: deleteError }] =
    useDeleteCustomFieldsMutation();

  const [queryParams, setQueryParams] = useState<IQueryParamsState>({
    ...defaultFilter,
    filters: [{ fieldname: 'createdAt', sort: 0 }],
  });
  const [_fieldList, setFieldList] = useState<IFieldItem[]>([]);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);

  const handleSubmitData = async (field: IHandleSubmitData) => {
    try {
      if (field.id) {
        // 🔹 Update existing custom field
        const originalField = _fieldList.find((f) => f.id === field.id);
        const updatedField = Object.fromEntries(
          Object.entries(field).filter(
            ([key, value]) => value !== undefined && value !== (originalField as any)[key]
          )
        ) as Partial<IHandleSubmitData>;
        if (Object.keys(updatedField).length > 0) {
          await updateCustomField({
            id: field.id,
            updates: { ...updatedField, module: moduleName },
          }).unwrap();
          toast.success(t('CUSTOM_FIELDS_UPDATED_SUCCESSFULLY'));
        }
      } else {
        // 🔹 Create new custom field
        delete field.id;
        await addCustomField({ ...field, module: moduleName }).unwrap();
        toast.success(t('CUSTOM_FIELDS_CREATED_SUCCESSFULLY'));
      }
      customFieldMenu.onFalse();
      setSelectedRecord?.(undefined);
      getCustomFieldListFunc({ ...queryParams, limit: pageLimit });
    } catch (err) {
      console.error('Error creating/updating custom field:', err);
    }
  };

  const handleDeleteCustomField = (ids: string[]) => {
    deleteCustomFields(ids)
      .then((res) => {
        toast.success(t('RECORD_DELETED_SUCCESSFULLY'));
        getCustomFieldListFunc({ ...queryParams, limit: pageLimit });
      })
      .catch((err) => {
        toast.error(err?.message ?? 'Record Deletion Failed.');
      });
  };

  const handleQueryChange = useCallback((newParams: QueryParams) => {
    setPageLimit(newParams.rowsPerPage);
    const transformedData: {
      filters: { fieldname: string; search?: string; sort?: number }[];
      globalSearch: string;
      page: number;
      limit: number;
      combineFieldsGroups?: any;
    } = {
      filters: sortFldList.reduce(
        (acc, field) => {
          const filterObj: { fieldname: string; search?: any; sort?: number } = {
            fieldname: field === 'labels' ? 'labels.labelName' : field,
          };

          const hasSearch =
            newParams.fieldSearch[field] !== undefined && newParams.fieldSearch[field] !== '';
          const hasSort = field === newParams.orderBy;

          if (hasSearch) {
            filterObj.search = newParams.fieldSearch[field];
          }

          if (hasSort) {
            filterObj.sort = newParams.order === 'asc' ? 0 : 1;
          }

          if (hasSearch || hasSort) {
            if (filterObj.fieldname === 'labels.labelName') {
              acc.push({ ...filterObj, search: filterObj?.search?.labelName || '' });
            } else {
              acc.push(filterObj);
            }
          }

          return acc;
        },
        [] as { fieldname: string; search?: string; sort?: number }[]
      ),
      globalSearch: newParams.globalSearch || '',
      page: newParams.page + 1,
      limit: newParams.rowsPerPage,
    };

    transformedData.filters.push({ fieldname: 'createdAt', sort: 1 });

    setQueryParams((prevState) => {
      const prevData = JSON.stringify(prevState);
      const newData = JSON.stringify(transformedData);

      return prevData !== newData ? transformedData : prevState;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getCustomFieldListFunc = useCallback(
    (params: IQueryParamsState) => {
      getCustomFields({ ...params, module: moduleName ?? '' })
        .unwrap()
        .then((res) => {
          setTotalPageCount(res.total);
          const filteredList = (list: IFieldItem[] = []) =>
            list.filter((listItem) => listItem.isVisible);
          setFieldList(
            isCustomFieldPage ? (res.list as IFieldItem[]) : filteredList(res.list as IFieldItem[])
          );
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [moduleName]
  );

  useEffect(() => {
    if (moduleName) getCustomFieldListFunc(queryParams);
  }, [queryParams, moduleName, getCustomFieldListFunc]);

  useEffect(() => {
    const errors = [fetchError, createError, updateError, deleteError].filter(Boolean);

    if (errors.length > 0) {
      const firstError = errors[0] as any;
      toast.error(firstError?.data?.message || t('SOMETHING_WENT_WRONG'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchError, createError, updateError, deleteError]);

  return {
    _fieldList,
    totalPageCount,
    getFieldsState: { isFetching, fetchError }, // Api call states
    addFieldState: { isCreating, createError },
    updateFieldState: { isUpdating, updateError },
    deleteFieldState: { isDeleting, deleteError },
    customFieldMenu,
    handleQueryChange,
    handleSubmitData,
    handleDeleteCustomField,
  };
}
